import React, { useState } from "react";
import { Loader2, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const CONTENTS = ["all", "markup", "none", "resolved"];

const Check = ({ id, checked, onChange, label, hint }) => (
    <label htmlFor={id} className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox id={id} data-testid={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5 border-slate-500" />
        <span className="text-sm text-slate-200">{label}{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
    </label>
);

export function ExportPdfDialog({ open, onOpenChange, onExport, exporting, t }) {
    const [content, setContent] = useState("all");
    const [flatten, setFlatten] = useState(true);
    const [annots, setAnnots] = useState(true);
    const [bubbles, setBubbles] = useState(true);
    const [summary, setSummary] = useState(false);
    const method = flatten && annots ? "both" : flatten ? "flatten" : "annot";
    const submit = () => onExport({ content, method, includeBubbles: bubbles, includeSummary: summary });
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="export-pdf-dialog" className="bg-[#111827] border-[#1e293b] text-slate-100 max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-mono">{t("ex_title")}</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">{t("ex_sub")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-5">
                    <section>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-mono">{t("ex_content")}</div>
                        <RadioGroup value={content} onValueChange={setContent} className="gap-2">
                            {CONTENTS.map((k) => (
                                <div key={k} className="flex items-center gap-2.5">
                                    <RadioGroupItem id={`ex-content-${k}`} data-testid={`ex-content-${k}`} value={k} className="border-slate-500 text-blue-400" />
                                    <Label htmlFor={`ex-content-${k}`} className="text-sm text-slate-200 cursor-pointer">{t(`ex_${k}`)}</Label>
                                </div>
                            ))}
                        </RadioGroup>
                    </section>
                    <section className="space-y-2.5">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{t("ex_method")}</div>
                        <Check id="ex-flatten" checked={flatten} onChange={setFlatten} label={t("ex_flatten")} hint={t("ex_flatten_hint")} />
                        <Check id="ex-annots" checked={annots} onChange={setAnnots} label={t("ex_annots")} hint={t("ex_annots_hint")} />
                        {flatten && annots && <div data-testid="ex-both-note" className="text-xs text-blue-300 pl-6">{t("ex_both")}</div>}
                    </section>
                    <section className="space-y-2.5 border-t border-[#1e293b] pt-4">
                        <Check id="ex-bubbles" checked={bubbles} onChange={setBubbles} label={t("ex_bubbles")} />
                        <Check id="ex-summary" checked={summary} onChange={setSummary} label={t("ex_summary")} />
                    </section>
                </div>
                <Button data-testid="ex-submit" disabled={exporting || (!flatten && !annots)} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-500 text-white mt-2">
                    {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} {t("ex_btn")}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
